import { proxyAdminIntoOrganization } from "@/api";
import {
    useMutation,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router";

import {
    SHARED_CONVERSATION_LOOKUP_ROOT,
    SHARED_CONVERSATIONS_KEY,
} from "@/api/chat/conversationListKeys";

const UNPREFIXED_ORGANIZATION_ROOTS = new Set([
    ...SHARED_CONVERSATIONS_KEY,
    SHARED_CONVERSATION_LOOKUP_ROOT,
    "conversations",
]);

/** Remove the cached queries that hold the current organization's data before an admin proxy switch. */
export const removeOrganizationQueries = (queryClient: QueryClient) =>
    queryClient.removeQueries({
        predicate: ({ queryKey: [root] }) =>
            typeof root === "string" &&
            (root.startsWith("frontend-") ||
                UNPREFIXED_ORGANIZATION_ROOTS.has(root)),
    });

interface UseOrganizationProxyOptions {
    /**
     * Custom navigation path after successful proxy
     * @default "/organizations/home"
     */
    navigationPath?: string;
    /**
     * Custom success message
     */
    successMessage?: string;
    /**
     * Custom error message
     * @default "Failed to go to organization"
     */
    errorMessage?: string;
    /**
     * Custom onSuccess callback
     */
    onSuccess?: () => void;
    /**
     * Whether to clear frontend queries on proxy
     * @default true
     */
    clearFrontendQueries?: boolean;
}

/**
 * Reusable hook for proxying admin users into an organization context
 */
export const useOrganizationProxy = (
    options: UseOrganizationProxyOptions = {}
) => {
    const {
        navigationPath = "/organizations/home",
        successMessage,
        errorMessage = "Failed to go to organization",
        onSuccess: customOnSuccess,
        clearFrontendQueries = true,
    } = options;

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {
        mutate: proxyIntoOrganization,
        isPending,
        isError,
        error,
    } = useMutation({
        mutationFn: (organizationId: number) =>
            proxyAdminIntoOrganization(organizationId),
        onSuccess: () => {
            // Clear frontend queries to ensure fresh data for the organization
            if (clearFrontendQueries) {
                removeOrganizationQueries(queryClient);
            }

            // Show success message if provided
            if (successMessage) {
                enqueueSnackbar(successMessage, {
                    variant: "success",
                });
            }

            // Call custom onSuccess callback if provided
            if (customOnSuccess) {
                customOnSuccess();
            }

            // Navigate to the organization
            navigate(navigationPath);
        },
        onError: () => {
            enqueueSnackbar(errorMessage, {
                variant: "error",
            });
        },
    });

    return {
        proxyIntoOrganization,
        isPending,
        isError,
        error,
    };
};
